import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { environment } from '../config/environment';

interface NewMagicLinkFormProps {
  jiraKey: string;
  customerEmail: string;
  supportContact?: {
    email: string;
    phone: string;
    hours: string;
    versiRent?: {
      email: string;
      phone: string;
      hours: string;
    };
    idealOne?: {
      email: string;
      phone: string;
      hours: string;
    };
  };
  onMagicLinkGenerated?: (email: string) => void;
}

export function NewMagicLinkForm({ 
  jiraKey, 
  customerEmail, 
  supportContact,
  onMagicLinkGenerated 
}: NewMagicLinkFormProps) {
  const [email, setEmail] = useState(customerEmail);
  const [loading, setLoading] = useState(false);

  // Default support contact info if not provided
  const defaultSupport = {
    email: 'support@idealss.com',
    phone: '+1 (555) 123-4567',
    hours: 'Mon-Fri 9AM-6PM EST',
    versiRent: {
      email: 'fsdsupport@idealss.com',
      phone: '601-703-0324',
      hours: 'Mon-Fri 9AM-6PM EST (Extended hours available at additional cost)'
    },
    idealOne: {
      email: 'fecsupport@idealss.com',
      phone: '601-703-0165',
      hours: '24/7/364 (Just Christmas is a real Holiday)'
    }
  };

  const supportInfo = supportContact || defaultSupport;
  
  const handleGenerateNewMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    
    try {
      // Use environment detection to determine correct URL
      const functionsUrl = environment.useEmulators 
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/generateNewMagicLink`
        : `https://us-central1-ima-onboard.cloudfunctions.net/generateNewMagicLink`;
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, jiraKey }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Call the callback if provided - the parent component will handle the success message
        if (onMagicLinkGenerated) {
          onMagicLinkGenerated(email);
        }
      } else {
        toast.error(result.error || 'Failed to generate new magic link');
      }
    } catch (error) {
      console.error('Error generating new magic link:', error);
      toast.error('Failed to generate new magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Generate New Magic Link
      </h3>
      
      <p className="text-sm text-gray-600 mb-4">
        Need a new magic link? Enter your email address below and we'll send you a fresh link for secure access.
      </p>

      <form onSubmit={handleGenerateNewMagicLink} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter email address"
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send New Magic Link'}
        </button>
      </form>

      <div className="mt-4 p-3 bg-blue-50 rounded-md">
        <p className="text-sm text-blue-800">
          <strong>Security Note:</strong> The new magic link will be sent to your email address and will be valid for 7 days. 
          This ensures only you can access your project securely.
        </p>
      </div>

      {/* Support Contact Section */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Need help?</span>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 mb-3">
            If you're having trouble accessing your project, please contact support:
          </p>
          
          {/* VersiRent Support - Only show if versiRent data exists */}
          {supportInfo.versiRent && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">VersiRent Support</h4>
              <div className="space-y-1 text-xs text-gray-600">
                <p><strong>Email:</strong> {supportInfo.versiRent.email}</p>
                <p><strong>Phone:</strong> {supportInfo.versiRent.phone}</p>
                <p><strong>Hours:</strong> {supportInfo.versiRent.hours}</p>
              </div>
            </div>
          )}

          {/* IdealOne Support - Only show if idealOne data exists */}
          {supportInfo.idealOne && (
            <div className="p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">IdealOne Support</h4>
              <div className="space-y-1 text-xs text-gray-600">
                <p><strong>Email:</strong> {supportInfo.idealOne.email}</p>
                <p><strong>Phone:</strong> {supportInfo.idealOne.phone}</p>
                <p><strong>Hours:</strong> {supportInfo.idealOne.hours}</p>
              </div>
            </div>
          )}

          {/* Show main support contact if no service-specific contacts exist */}
          {!supportInfo.versiRent && !supportInfo.idealOne && (
            <div className="p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                {supportInfo.email === 'fsdsupport@idealss.com' ? 'VersiRent Support' : 
                 supportInfo.email === 'fecsupport@idealss.com' ? 'IdealOne Support' : 
                 'General Support'}
              </h4>
              <div className="space-y-1 text-xs text-gray-600">
                <p><strong>Email:</strong> {supportInfo.email}</p>
                <p><strong>Phone:</strong> {supportInfo.phone}</p>
                <p><strong>Hours:</strong> {supportInfo.hours}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
